/**
 * Central JSON-LD builders.
 *
 * Every LocalBusiness / Service / FAQPage block on the site should come from
 * here so an address, phone or area change is a single edit and the nodes stay
 * consistent (and share stable @id values that search engines can link).
 */

import {
  businessInfo,
  postalAddressSchema,
  primaryAreas,
} from "@/data/businessInfo";
import { reviewSchema } from "@/data/testimonials";

export const SITE_URL = businessInfo.url;
export const ORG_ID = `${SITE_URL}/#organisation`;

const SAME_AS = [
  "https://www.facebook.com/infinityfsuk",
  "https://www.linkedin.com/company/infinityfsuk",
  "https://www.instagram.com/infinityfsuk/",
];

/** The canonical LocalBusiness (AccountingService) node for the firm. */
export function localBusinessSchema(options?: { withReviews?: boolean }) {
  return {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    "@id": ORG_ID,
    name: businessInfo.name,
    legalName: businessInfo.legalName,
    url: SITE_URL,
    logo: businessInfo.logo,
    image: businessInfo.ogImage,
    description:
      "AAT Licensed accountants in Salisbury, the New Forest and Dorset. Bookkeeping, tax, payroll, VAT, management accounts and business advisory for growing SMEs.",
    foundingDate: "2022",
    telephone: businessInfo.telephoneE164,
    email: businessInfo.email,
    address: postalAddressSchema,
    geo: {
      "@type": "GeoCoordinates",
      latitude: businessInfo.geo.lat,
      longitude: businessInfo.geo.lng,
    },
    hasMap: businessInfo.url + "/contact",
    areaServed: primaryAreas.map((name) => ({ "@type": "Place", name })),
    serviceType: [
      "Bookkeeping",
      "Tax Returns",
      "Payroll",
      "VAT",
      "Management Accounts",
      "Business Advisory",
      "Fractional Finance Director",
    ],
    priceRange: "££",
    currenciesAccepted: "GBP",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
    sameAs: SAME_AS,
    ...(options?.withReviews ? { review: reviewSchema(businessInfo.name) } : {}),
  };
}

/** Reusable provider reference for Service nodes. */
export const providerRef = {
  "@type": "AccountingService",
  "@id": ORG_ID,
  name: businessInfo.legalName,
  telephone: businessInfo.telephoneE164,
  email: businessInfo.email,
  address: postalAddressSchema,
};

export function serviceSchema(input: {
  name: string;
  url: string;
  description: string;
  areaServed?: string[];
  offers?: { name: string; description?: string }[];
}) {
  const areas = input.areaServed ?? ["Wiltshire", "Hampshire", "Dorset"];
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${input.url}#service`,
    name: input.name,
    serviceType: input.name,
    url: input.url,
    description: input.description,
    provider: providerRef,
    areaServed: [
      ...areas.map((name) => ({ "@type": "Place", name })),
      { "@type": "Country", name: "United Kingdom" },
    ],
    ...(input.offers && input.offers.length
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${input.name} — what's included`,
            itemListElement: input.offers.map((o) => ({
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: o.name,
                ...(o.description ? { description: o.description } : {}),
              },
            })),
          },
        }
      : {}),
  };
}

export function faqPageSchema(
  faqs: { q: string; a: string }[],
  url?: string,
) {
  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(url ? { "@id": `${url}#faq` } : {}),
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
